import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  Check,
  Clock3,
  Compass,
  Grid3x3,
  HeartHandshake,
  Home,
  Shield,
  Sparkles,
  Star,
  Sun,
  Wallet,
} from 'lucide-react';
import axios from 'axios';

import HomeCalculators from '../components/HomeCalculators';

const gold = '#C9A961';
const surface = '#101632';
const rose = '#F87171';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55 },
};

const ZodiacLineArt = ({ className = '', opacity = 0.12 }) => (
  <svg
    viewBox="0 0 600 600"
    className={className}
    style={{ opacity }}
    aria-hidden="true"
  >
    <circle cx="300" cy="300" r="245" fill="none" stroke="rgba(201,169,97,0.35)" strokeWidth="1.2" />
    <circle cx="300" cy="300" r="190" fill="none" stroke="rgba(201,169,97,0.18)" strokeWidth="1" />
    <circle cx="300" cy="300" r="130" fill="none" stroke="rgba(201,169,97,0.12)" strokeWidth="1" />
    {[...Array(12)].map((_, i) => {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      const x = 300 + Math.cos(angle) * 245;
      const y = 300 + Math.sin(angle) * 245;
      const lineX = 300 + Math.cos(angle) * 130;
      const lineY = 300 + Math.sin(angle) * 130;
      const glyphs = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
      return (
        <g key={i}>
          <line x1="300" y1="300" x2={lineX} y2={lineY} stroke="rgba(201,169,97,0.14)" strokeWidth="1" />
          <text x={x} y={y} fill="rgba(201,169,97,0.5)" textAnchor="middle" dominantBaseline="middle" fontSize="20">
            {glyphs[i]}
          </text>
        </g>
      );
    })}
  </svg>
);

export default function HomePage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get('/api/pricing').then((r) => {
      if (r.data?.plans) setPricing(r.data.plans);
    }).catch(() => {});
    // Auto-scroll to #free-calculators if hash is present
    if (window.location.hash === '#free-calculators') {
      setTimeout(() => document.getElementById('free-calculators')?.scrollIntoView({ behavior: 'smooth' }), 600);
    }
  }, []);

  const getPrice = (id) => {
    const dynamicPrice = pricing?.[id]?.display_price;
    if (typeof dynamicPrice === 'number') return dynamicPrice;
    if (typeof dynamicPrice === 'string') return Number(dynamicPrice) || 0;
    if (id === 'bundle') return 1499;
    return 599;
  };

  const bundlePrice = getPrice('bundle');
  const combinedPrice = getPrice('numerology') + getPrice('astrology') + getPrice('vastu') + getPrice('weekly_report');

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const highSpendStories = [
    {
      icon: <Home className="w-5 h-5" />,
      title: 'Vastu guidance in the market can become very expensive',
      market: 'Often ₹11,000 to ₹21,000 for one home visit',
      pain: 'During financially tight phases, repeated expert visits can feel heavy on families.',
      astrowins: `Astrowins includes practical Vastu stabilization inside the full plan at ₹${bundlePrice}.`,
    },
    {
      icon: <Grid3x3 className="w-5 h-5" />,
      title: 'Name correction is often offered at premium rates',
      market: 'Can go up to ₹51,000 for one correction cycle',
      pain: 'People seeking quick relief may end up paying more than they planned.',
      astrowins: `Name correction support is included in the Astrowins lifetime plan at ₹${bundlePrice}.`,
    },
    {
      icon: <Sun className="w-5 h-5" />,
      title: 'Astrology consultations can become a recurring cost',
      market: 'New sessions are often needed for each tough phase',
      pain: 'Many people keep paying just to get clarity for the next decision.',
      astrowins: `Astrology insights, remedies and timing intelligence stay available in one system for ₹${bundlePrice}.`,
    },
    {
      icon: <Compass className="w-5 h-5" />,
      title: 'Fragmented advice across different experts creates confusion',
      market: 'Different expert, different fee, different recommendation',
      pain: 'Even after spending more, daily decision clarity may still feel missing.',
      astrowins: `Astrology + Vastu + Numerology + Weekly Forecast together. One-time ₹${bundlePrice}.`,
    },
  ];

  const modules = [
    {
      id: 'astrology',
      name: 'Astrology Engine',
      icon: <Sun className="w-5 h-5" />,
      headline: 'Not just prediction — decision timing companion',
      points: ['Swiss Ephemeris precision', 'Risk window alerts', 'Remedy-backed daily actions'],
    },
    {
      id: 'vastu',
      name: 'Vastu Intelligence',
      icon: <Home className="w-5 h-5" />,
      headline: 'Not just room advice — energy correction companion',
      points: ['Space imbalance detection', 'Low-cost corrective steps', 'Priority fixes before major losses'],
    },
    {
      id: 'numerology',
      name: 'Numerology Intelligence',
      icon: <Grid3x3 className="w-5 h-5" />,
      headline: 'Not just numbers — personal pattern companion',
      points: ['Driver & conductor mapping', 'Name and date optimization', 'Personalized action windows'],
    },
    {
      id: 'weekly_report',
      name: 'Weekly Live Forecast',
      icon: <Compass className="w-5 h-5" />,
      headline: 'Not just report — daily caution and remedy companion',
      points: ['Do/Avoid guidance day-wise', 'Nakshatra-based action cues', 'Advance watch for future vulnerabilities'],
    },
  ];

  const testimonials = [
    {
      quote: 'I was paying separate consultants every month. Astrowins gave one place, one logic, one action path.',
      person: 'Kunal P.',
      role: 'Retail Business Owner',
    },
    {
      quote: 'What touched me most: it does not overwhelm you. It shows exactly what to do now to reduce risk.',
      person: 'Nidhi S.',
      role: 'HR Manager',
    },
    {
      quote: 'For my family this became a weekly accountability system. We follow the guidance before things get worse.',
      person: 'Rajat V.',
      role: 'Startup Operator',
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#080D26', fontFamily: 'Manrope, sans-serif' }}>
      <header className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl" style={{ background: 'rgba(8,13,38,0.88)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Astrowins" className="h-10 md:h-12 w-auto" data-testid="homepage-logo" />
            <span className="text-white text-lg md:text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>ASTROWINS</span>
          </div>
          <div className="flex items-center gap-2.5">
            <button data-testid="header-login-btn" onClick={() => navigate('/auth')} className="text-slate-300 hover:text-white text-sm font-medium px-3 py-2">Login</button>
            <button data-testid="header-crazy-offer-btn" onClick={() => scrollTo('pricing')} className="px-4 py-2.5 rounded-lg font-semibold text-sm" style={{ background: gold, color: '#080D26' }}>
              Get Started
            </button>
          </div>
        </div>
      </header>

      <section className="pt-28 pb-10 md:pt-36 md:pb-14 px-6 relative overflow-hidden">
        <ZodiacLineArt className="absolute -right-24 -top-28 w-[420px] h-[420px] pointer-events-none" opacity={0.14} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.07]" style={{ background: `radial-gradient(circle, ${gold} 0%, transparent 70%)` }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div {...fadeUp} className="mb-6">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase border" style={{ color: gold, borderColor: 'rgba(201,169,97,0.3)', background: 'rgba(201,169,97,0.08)' }} data-testid="hero-fomo-badge">
              Your Personal Energy Companion
            </span>
          </motion.div>

          <motion.h1 {...fadeUp} transition={{ delay: 0.1, duration: 0.7 }} className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="hero-main-headline">
            <span className="text-white">Stop Guessing.</span>
            <br />
            <span style={{ color: gold }}>Start Winning Every Day.</span>
          </motion.h1>

          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-base md:text-xl leading-relaxed text-slate-300 max-w-2xl mx-auto" data-testid="hero-subheadline">
            Not another prediction app. A structured daily guide that integrates Astrology, Numerology & Vastu into one intelligent dashboard — so you make sharper decisions, every single day.
          </motion.p>
        </div>
      </section>

      {/* Interactive Calculators — Engagement Hook */}
      <HomeCalculators onCTA={() => scrollTo('pricing')} />

      <section id="story-pressure" className="py-20 px-5 relative overflow-hidden" style={{ background: surface }}>
        <ZodiacLineArt className="absolute -left-24 top-24 w-[340px] h-[340px] pointer-events-none" opacity={0.08} />
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs tracking-widest uppercase font-bold" style={{ color: gold }}>Story-driven reality check</p>
            <h2 data-testid="story-section-heading" className="text-3xl md:text-5xl font-bold text-white mt-3 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              What many families experience in the market — and how Astrowins simplifies it
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg">
              Ancient wisdom should protect families, not financially pressure them.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {highSpendStories.map((item, idx) => (
              <motion.div key={idx} {...fadeUp} transition={{ delay: idx * 0.06 }} className="rounded-2xl border p-6" style={{ borderColor: 'rgba(255,255,255,0.09)', background: 'rgba(255,255,255,0.03)' }} data-testid={`pain-story-card-${idx}`}>
                <div className="flex items-center gap-2 mb-3" style={{ color: gold }}>
                  {item.icon}
                  <span className="font-semibold text-sm uppercase tracking-wide">Case</span>
                </div>
                <h3 className="text-white text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 line-through mb-2">Typical spend: {item.market}</p>
                <p className="text-sm text-rose-300 mb-3">{item.pain}</p>
                <p className="text-sm text-green-300 leading-relaxed">{item.astrowins}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border p-6" style={{ borderColor: 'rgba(248,113,113,0.35)', background: 'rgba(248,113,113,0.08)' }} data-testid="heart-hit-lines">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                'Before a difficult phase peaks, informed decisions matter the most.',
                'Late guidance often becomes expensive guidance.',
                'A small correction today can protect you from a bigger setback tomorrow.',
              ].map((line, i) => (
                <p key={i} className="text-sm text-rose-100 font-medium leading-relaxed">“{line}”</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-5 relative overflow-hidden">
        <ZodiacLineArt className="absolute right-[-120px] bottom-[-140px] w-[420px] h-[420px] pointer-events-none" opacity={0.09} />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 items-start">
          <motion.div {...fadeUp}>
            <p className="text-xs tracking-widest uppercase font-bold" style={{ color: gold }}>Companion model, not report model</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Each module stays with you as a guide, not a one-time PDF.
            </h2>
            <p className="text-slate-300 text-base leading-relaxed mb-5">
              We tell you what stars and energies are indicating, so you can take informed decisions in money, health, career and relationships.
            </p>
            <div className="rounded-2xl border p-6 relative overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)' }} data-testid="companion-illustration">
              <ZodiacLineArt className="absolute inset-0 w-full h-full pointer-events-none" opacity={0.1} />
              <div className="relative z-10 space-y-3">
                <p className="text-slate-200 text-sm leading-relaxed">
                  Astrowins interprets planetary movement, number patterns, and spatial signals into practical daily decisions.
                </p>
                <p className="text-slate-300 text-sm leading-relaxed">
                  The goal is simple: you stay proactive, informed, and protected before bigger difficulties build up.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {['Timing clarity', 'Practical remedies', 'Weekly accountability', 'Lifetime access'].map((chip, idx) => (
                    <span key={idx} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: 'rgba(201,169,97,0.35)', color: '#E8D6A8', background: 'rgba(201,169,97,0.08)' }}>
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {modules.map((module, idx) => (
              <motion.div key={module.id} {...fadeUp} transition={{ delay: idx * 0.06 }} className="rounded-2xl border p-5 flex flex-col" style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(16,22,50,0.9)' }} data-testid={`module-card-${module.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ color: gold, background: 'rgba(201,169,97,0.12)' }}>
                  {module.icon}
                </div>
                <h3 className="text-white font-bold text-lg mb-1">{module.name}</h3>
                <p className="text-sm text-slate-300 mb-3">{module.headline}</p>
                <ul className="space-y-2 mb-4">
                  {module.points.map((point, pIndex) => (
                    <li key={pIndex} className="text-xs text-slate-400 flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5" style={{ color: gold }} />
                      {point}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex items-center justify-between">
                  <p className="text-white font-bold" data-testid={`module-price-${module.id}`}>₹{getPrice(module.id)}</p>
                  <button data-testid={`module-buy-${module.id}`} onClick={() => navigate(`/checkout?plan=${module.id}`)} className="text-xs font-semibold" style={{ color: gold }}>
                    Get Module <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-5" style={{ background: surface }}>
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs tracking-widest uppercase font-bold" style={{ color: gold }}>Proof + emotional credibility</p>
            <h2 className="text-3xl md:text-5xl text-white font-bold mt-3 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              People buy Astrowins when they want relief from confusion and overpaying.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((item, idx) => (
              <motion.div key={idx} {...fadeUp} transition={{ delay: idx * 0.08 }} className="rounded-2xl border p-6" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }} data-testid={`sales-testimonial-${idx}`}>
                <div className="flex gap-1 mb-3">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 fill-current" style={{ color: gold }} />)}</div>
                <p className="text-slate-200 text-sm italic leading-relaxed mb-4">“{item.quote}”</p>
                <p className="text-white text-sm font-semibold">{item.person}</p>
                <p className="text-slate-500 text-xs">{item.role}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: <Clock3 className="w-4 h-4" />, text: 'One-time payment. Lifetime usage.' },
              { icon: <Wallet className="w-4 h-4" />, text: 'Avoid repeated consultant spending.' },
              { icon: <HeartHandshake className="w-4 h-4" />, text: 'Companion + accountability approach.' },
              { icon: <AlertTriangle className="w-4 h-4" />, text: 'Advance caution before difficult windows.' },
            ].map((item, idx) => (
              <div key={idx} className="rounded-xl p-3 border text-sm text-slate-300 flex gap-2 items-center" style={{ borderColor: 'rgba(201,169,97,0.2)', background: 'rgba(201,169,97,0.07)' }}>
                <span style={{ color: gold }}>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wider mb-4" style={{ borderColor: 'rgba(201,169,97,0.25)', color: gold }}>
              <Sparkles className="w-4 h-4" /> Crazy Offer Active
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              All 4 modules together for ₹{bundlePrice}
            </h2>
            <p className="text-slate-400 text-base md:text-lg">Astrology + Vastu + Numerology + Weekly Live Forecast. One payment. Lifetime access.</p>
          </motion.div>

          <div className="rounded-3xl border p-7 md:p-10 mb-8" style={{ borderColor: 'rgba(201,169,97,0.35)', background: 'rgba(201,169,97,0.05)' }} data-testid="bundle-offer-highlight">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-widest mb-2" style={{ color: gold }}>Offer comparison</p>
                <h3 className="text-2xl md:text-3xl text-white font-bold mb-3">Pay once, stop paying high-pressure prices repeatedly</h3>
                <div className="flex items-end gap-3 mb-4">
                  <p className="text-5xl font-bold" style={{ color: gold }} data-testid="bundle-live-price">₹{bundlePrice}</p>
                  <p className="text-slate-500 line-through text-2xl" data-testid="bundle-total-compare">₹{combinedPrice || 2396}</p>
                </div>
                <p className="text-sm text-slate-300 mb-5">Admin panel controls this live price. When admin updates pricing, this section updates automatically.</p>
                <button data-testid="bundle-buy-now" onClick={() => navigate('/checkout?plan=bundle')} className="px-8 py-4 rounded-xl font-bold text-base" style={{ background: gold, color: '#080D26' }}>
                  Get Lifetime Bundle Now <ArrowRight className="w-5 h-5 inline ml-1" />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {['Astrology Engine', 'Vastu Intelligence', 'Numerology Intelligence', 'Weekly Live Forecast', 'Daily Action Guidance', 'Advance Risk Alerts'].map((f, idx) => (
                  <div key={idx} className="rounded-lg px-3 py-2.5 border text-sm text-slate-200 flex items-center gap-2" style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}>
                    <Check className="w-4 h-4" style={{ color: gold }} />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {modules.map((module, idx) => (
              <motion.div key={module.id} {...fadeUp} transition={{ delay: idx * 0.07 }} className="rounded-2xl border p-5" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }} data-testid={`pricing-card-${module.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(201,169,97,0.12)', color: gold }}>{module.icon}</div>
                <h4 className="text-white font-bold text-base mb-1">{module.name}</h4>
                <p className="text-2xl text-white font-bold mb-3" data-testid={`pricing-card-price-${module.id}`}>₹{getPrice(module.id)}</p>
                <button data-testid={`pricing-card-buy-${module.id}`} onClick={() => navigate(`/checkout?plan=${module.id}`)} className="w-full py-2.5 rounded-lg border text-sm font-semibold" style={{ borderColor: 'rgba(201,169,97,0.35)', color: gold }}>
                  Choose {module.name}
                </button>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6" data-testid="admin-coupon-pricing-note">
            Pricing is connected to Admin Pricing Control. Coupon codes created in Admin Coupons are applied and validated on checkout.
          </p>
        </div>
      </section>

      <section className="py-20 px-5" style={{ background: surface }}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-5xl text-white font-bold leading-tight mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Ancient wisdom is for protection,
              <br />
              <span style={{ color: gold }}>not for financial pressure.</span>
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Astrowins helps you act before misery time, with practical and accountable guidance every week.
            </p>
            <button data-testid="final-home-cta" onClick={() => navigate('/checkout?plan=bundle')} className="px-10 py-4 rounded-xl font-bold text-base" style={{ background: gold, color: '#080D26' }}>
              Start Astrowins Lifetime Access
            </button>
          </motion.div>
        </div>
      </section>

      <footer className="py-16 px-6 border-t" style={{ background: '#070B1E', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo.png" alt="Astrowins" className="h-14 w-auto" data-testid="footer-logo" />
                <span className="text-xl text-white font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>ASTROWINS</span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">Your personal energy companion. Structured daily guidance through classical systems.</p>
              <div className="flex gap-3">
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:opacity-80" style={{ background: 'rgba(201,169,97,0.15)' }} aria-label="Facebook" data-testid="footer-facebook-link">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:opacity-80" style={{ background: 'rgba(201,169,97,0.15)' }} aria-label="Instagram" data-testid="footer-instagram-link">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:opacity-80" style={{ background: 'rgba(201,169,97,0.15)' }} aria-label="Twitter" data-testid="footer-twitter-link">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:opacity-80" style={{ background: 'rgba(201,169,97,0.15)' }} aria-label="YouTube" data-testid="footer-youtube-link">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Platform</h4>
              <ul className="space-y-2.5 text-sm">
                {[['Pricing', '/#pricing'], ['Login', '/auth'], ['Blog', '/blog'], ['Dashboard', '/dashboard'], ['Contact', '/contact']].map(([label, href]) => (
                  <li key={label}><a href={href} className="text-slate-500 hover:text-white transition-colors">{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                {[['Privacy Policy', '/privacy'], ['Terms', '/terms'], ['Refund Policy', '/refund'], ['Disclaimer', '/disclaimer']].map(([label, href]) => (
                  <li key={label}><a href={href} className="text-slate-500 hover:text-white transition-colors">{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Contact</h4>
              <ul className="space-y-2.5 text-sm text-slate-500">
                <li><a href="mailto:support@astrowins.in" className="hover:text-white transition-colors">support@astrowins.in</a></li>
                <li>India</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <p className="text-slate-600 text-xs">&copy; 2026 Astrowins. All rights reserved.</p>
            <div className="flex gap-6 text-xs">
              {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Refund', '/refund']].map(([l, h]) => (
                <a key={l} href={h} className="text-slate-600 hover:text-slate-400 transition-colors">{l}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
